import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Zap,
  CheckCircle2,
  XCircle,
  ArrowRight,
  RefreshCw,
  PartyPopper,
  UserPlus,
  FileText,
  Stethoscope,
  ShoppingCart,
  Pill,
  ClipboardCheck,
} from "lucide-react";

interface ActionItem {
  id: string;
  priority: "high" | "medium" | "low";
  category: string;
  icon: React.ReactNode;
  description: string;
  tab: string;
  inlineAction?: "approve_role" | "approve_provider";
  metadata?: any;
}

interface AdminActionQueueProps {
  onNavigateToTab: (tab: string) => void;
}

const AdminActionQueue = ({ onNavigateToTab }: AdminActionQueueProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [roleReqs, assessments, credentials, orders, prescriptions, qaIssues] =
      await Promise.all([
        supabase.from("role_requests").select("id, user_id, requested_role, metadata, created_at").eq("status", "pending"),
        supabase.from("health_assessments").select("id, user_id").eq("status", "pending"),
        supabase.from("provider_credentials").select("id, user_id, credential_status, license_expiry").in("credential_status", ["pending_review"]),
        supabase.from("orders").select("id, patient_name, status").eq("status", "pending"),
        supabase.from("prescriptions").select("id, peptide_name, patient_id").eq("status", "draft"),
        supabase.from("qa_issues").select("id, title, severity").eq("status", "open"),
      ]);

    // Fetch profile names for assessments
    const assessmentUserIds = assessments.data?.map((a) => a.user_id) || [];
    let profileMap: Record<string, string> = {};
    if (assessmentUserIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name")
        .in("user_id", assessmentUserIds);
      profiles?.forEach((p) => {
        profileMap[p.user_id] = [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown";
      });
    }

    const queue: ActionItem[] = [];

    // Role requests — HIGH
    roleReqs.data?.forEach((r) => {
      const meta = r.metadata as Record<string, any> | null;
      const name = [meta?.first_name, meta?.last_name].filter(Boolean).join(" ") || "Unknown";
      queue.push({
        id: `role-${r.id}`,
        priority: "high",
        category: "Role Request",
        icon: <UserPlus size={16} />,
        description: `${name} wants ${r.requested_role}`,
        tab: "invites",
        inlineAction: "approve_role",
        metadata: { requestId: r.id, userId: r.user_id, role: r.requested_role, meta: r.metadata },
      });
    });

    // Provider credentials — HIGH (with profile info for inline approve)
    const credUserIds = credentials.data?.map((c) => c.user_id) || [];
    let credProfileMap: Record<string, { name: string; email: string | null }> = {};
    if (credUserIds.length > 0) {
      const [{ data: credProfiles }, { data: credEmails }] = await Promise.all([
        supabase.from("profiles").select("user_id, first_name, last_name").in("user_id", credUserIds),
        supabase.rpc("get_user_emails"),
      ]);
      const emailMap: Record<string, string> = {};
      (credEmails || []).forEach((e) => { emailMap[e.user_id] = e.email; });
      credProfiles?.forEach((p) => {
        credProfileMap[p.user_id] = {
          name: [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown",
          email: emailMap[p.user_id] || null,
        };
      });
    }

    credentials.data?.forEach((c) => {
      const info = credProfileMap[c.user_id];
      queue.push({
        id: `cred-${c.id}`,
        priority: "high",
        category: "Provider Review",
        icon: <Stethoscope size={16} />,
        description: `${info?.name || "Provider"} — credential review`,
        tab: "providers",
        inlineAction: "approve_provider",
        metadata: { userId: c.user_id, providerName: info?.name, providerEmail: info?.email },
      });
    });

    // Assessments — MEDIUM
    assessments.data?.forEach((a) => {
      queue.push({
        id: `assess-${a.id}`,
        priority: "medium",
        category: "Assessment",
        icon: <FileText size={16} />,
        description: `${profileMap[a.user_id] || "Patient"} — pending review`,
        tab: "patients",
      });
    });

    // Orders — MEDIUM
    orders.data?.forEach((o) => {
      queue.push({
        id: `order-${o.id}`,
        priority: "medium",
        category: "Order",
        icon: <ShoppingCart size={16} />,
        description: `${o.patient_name || "Unknown"} — pending`,
        tab: "orders",
      });
    });

    // Draft prescriptions — LOW
    prescriptions.data?.forEach((p) => {
      queue.push({
        id: `rx-${p.id}`,
        priority: "low",
        category: "Prescription",
        icon: <Pill size={16} />,
        description: `${p.peptide_name} draft`,
        tab: "providers",
      });
    });

    // QA issues — MEDIUM
    qaIssues.data?.forEach((q) => {
      queue.push({
        id: `qa-${q.id}`,
        priority: q.severity === "critical" ? "high" : "medium",
        category: "QA Issue",
        icon: <ClipboardCheck size={16} />,
        description: q.title,
        tab: "qa",
      });
    });

    // Sort: high first, then medium, then low
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    queue.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    setItems(queue);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60_000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleApproveRole = async (item: ActionItem) => {
    if (!user || !item.metadata) return;
    const { requestId, userId, role, meta } = item.metadata;

    // Provider role MUST go through the credential review flow. The provider
    // first submits credentials, then the "Provider Review" item appears in
    // this queue with inlineAction=approve_provider, which calls the
    // approve_provider RPC after validating BAA + NPI + W-9.
    if (role === "provider") {
      toast({
        title: "Use the credential review flow",
        description:
          "Provider role can only be granted after credentials are submitted. Ask the applicant to complete onboarding at /provider/register, then approve them from the Provider Review queue.",
        variant: "destructive",
      });
      return;
    }

    setActing(item.id);

    await supabase
      .from("role_requests")
      .update({ status: "approved", reviewed_by: user.id, reviewed_at: new Date().toISOString() })
      .eq("id", requestId);

    const { error: roleError } = await supabase
      .from("user_roles")
      .insert({ user_id: userId, role: role as any });
    if (roleError) {
      toast({ title: "Approval failed", description: roleError.message, variant: "destructive" });
      setActing(null);
      return;
    }

    if (role === "pharmacy" && meta?.pharmacy_name) {
      const { data: pharmacy } = await supabase
        .from("partner_pharmacies")
        .insert({
          name: meta.pharmacy_name,
          email: meta.email || "",
          state: meta.state || null,
          city: meta.city || null,
          address: meta.address || null,
          zip_code: meta.zip_code || null,
          phone: meta.phone || null,
        })
        .select("id")
        .single();
      if (pharmacy) {
        await supabase.from("pharmacy_staff").insert({ user_id: userId, pharmacy_id: pharmacy.id, role: "admin" });
      }
    }

    if (role === "salesperson") {
      await supabase.from("salesperson_settings").insert({ user_id: userId });
    }

    // Send welcome email for salesperson or pharmacy approvals
    if (role === "salesperson" || role === "pharmacy") {
      const { data: emails } = await supabase.rpc("get_user_emails");
      const recipientEmail = (emails || []).find((e) => e.user_id === userId)?.email;
      if (recipientEmail) {
        const recipientName = [meta?.first_name, meta?.last_name].filter(Boolean).join(" ") || undefined;
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "role-approved-welcome",
            recipientEmail,
            idempotencyKey: `role-approved-${requestId}`,
            templateData: {
              recipientName,
              approvedRole: role,
            },
          },
        });
      }
    }

    toast({ title: "Approved", description: `${role} role granted.` });
    setActing(null);
    fetchAll();
  };

  const handleRejectRole = async (item: ActionItem) => {
    if (!user || !item.metadata) return;
    setActing(item.id);
    await supabase
      .from("role_requests")
      .update({ status: "rejected", reviewed_by: user.id, reviewed_at: new Date().toISOString() })
      .eq("id", item.metadata.requestId);
    toast({ title: "Rejected" });
    setActing(null);
    fetchAll();
  };

  const handleApproveProvider = async (item: ActionItem) => {
    if (!user || !item.metadata) return;
    setActing(item.id);
    const { userId, providerName, providerEmail } = item.metadata;

    const { error } = await (supabase.rpc as any)("approve_provider", { target_user_id: userId });
    if (error) {
      toast({ title: "Approval failed", description: error.message, variant: "destructive" });
      setActing(null);
      return;
    }

    // Send welcome email
    if (providerEmail) {
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "role-approved-welcome",
          recipientEmail: providerEmail,
          idempotencyKey: `role-approved-provider-${userId}`,
          templateData: { recipientName: providerName, approvedRole: "provider" },
        },
      });
    }

    // Update role_request status if one exists
    await supabase
      .from("role_requests")
      .update({ status: "approved", reviewed_by: user.id, reviewed_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("requested_role", "provider")
      .eq("status", "pending");

    toast({ title: "Provider approved", description: `${providerName || "Provider"} now has provider access.` });
    setActing(null);
    fetchAll();
  };

  const handleRejectProvider = async (item: ActionItem) => {
    if (!user || !item.metadata) return;
    setActing(item.id);
    const { userId } = item.metadata;

    const { error } = await (supabase.rpc as any)("revoke_provider", { target_user_id: userId, reason: "rejected" });
    if (error) {
      toast({ title: "Rejection failed", description: error.message, variant: "destructive" });
      setActing(null);
      return;
    }

    toast({ title: "Provider rejected" });
    setActing(null);
    fetchAll();
  };

  const priorityColor = {
    high: "bg-destructive/15 text-destructive",
    medium: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
    low: "bg-primary/10 text-primary",
  };

  const priorityDot = {
    high: "bg-destructive",
    medium: "bg-yellow-500",
    low: "bg-primary",
  };

  return (
    <Card className="border-border/50 mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap size={18} className="text-primary" /> Action Queue
            {items.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {items.length}
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={fetchAll} disabled={loading} className="h-8 w-8">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && items.length === 0 ? (
          <div className="flex justify-center py-6">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
            <PartyPopper size={28} className="text-primary" />
            <p className="text-sm font-medium">All caught up!</p>
            <p className="text-xs">No pending items require your attention.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
              >
                {/* Priority dot */}
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${priorityDot[item.priority]}`} />

                {/* Icon */}
                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${priorityColor[item.priority]}`}>
                  {item.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">
                      {item.category}
                    </Badge>
                    <span className="text-sm truncate">{item.description}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {(item.inlineAction === "approve_role" || item.inlineAction === "approve_provider") && (
                    <>
                      <Button
                        size="sm"
                        variant="default"
                        className="h-7 px-2.5 text-xs gap-1"
                        disabled={acting === item.id}
                        onClick={() =>
                          item.inlineAction === "approve_provider"
                            ? handleApproveProvider(item)
                            : handleApproveRole(item)
                        }
                      >
                        <CheckCircle2 size={12} /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2.5 text-xs gap-1"
                        disabled={acting === item.id}
                        onClick={() =>
                          item.inlineAction === "approve_provider"
                            ? handleRejectProvider(item)
                            : handleRejectRole(item)
                        }
                      >
                        <XCircle size={12} />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => onNavigateToTab(item.tab)}
                  >
                    <ArrowRight size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminActionQueue;
